import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Tooltip, Typography } from '@material-ui/core';
import { List as ImmutableList } from 'immutable';
import { useAppDispatch } from 'store/hooks';
import { DialogComponent, Table } from 'common/components/index';
import notesMessages from '../messages/notes';
import * as actions from 'common/components/PersonProfile/state/actions';
import { IHeadCell, ITableParams, ITableRow } from 'common/interfaces/table';
import useRootSelector from 'common/hooks/useRootSelector';
import * as selectors from 'common/components/PersonProfile/state/selectors';
import tableHeaders from 'common/messages/tableHeaders';
import { INote } from 'common/components/PersonProfile/interfaces';
import EmployeeLinkWithAvatar from 'common/components/ProfileLinkWithAvatar/EmployeeLinkWithAvatar';
import { makeTableParams } from 'common/utils/http';
import { PeakModuleForNewPersonType } from 'common/interfaces/steps';
import { IPageMetaImt } from 'common/interfaces/pagination';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import { DEFAULT_DATE_TIME_FORMAT } from 'common/constants/dateFormats';

const headerOptions: IHeadCell[] = [
  {
    id: 'author',
    label: <FormattedMessage {...tableHeaders.author} />,
    sort: true,
  },
  {
    id: 'noteCode',
    label: <FormattedMessage {...tableHeaders.noteCode} />,
    sort: true,
  },
  {
    id: 'alertAtNextCheckIn',
    label: <FormattedMessage {...tableHeaders.alertAtNextCheckIn} />,
    sort: true,
  },
  {
    id: 'text',
    label: <FormattedMessage {...tableHeaders.text} />,
    sort: true,
  },
  {
    id: 'createdDate',
    label: <FormattedMessage {...tableHeaders.createdDate} />,
    sort: true,
  },
];

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  personId: number;
  module: PeakModuleForNewPersonType;
}

const ViewAllNotesModal = ({ isOpen, onClose, personId, module }: IProps) => {
  const dispatch = useAppDispatch();
  const [timezoneMoment] = useTimezoneMoment();

  const selectNotes = selectors.makeSelectPersonNotes();
  const selectNotesMeta = selectors.makeSelectPersonNotesMeta();
  const selectNotesIsLoading = selectors.makeSelectPersonNotesLoading();

  const notes: ImmutableList<INote> = useRootSelector(state =>
    selectNotes(state, { customerId: personId }),
  );
  const notesMeta: IPageMetaImt = useRootSelector(state =>
    selectNotesMeta(state, { customerId: personId }),
  );
  const notesIsLoading: boolean = useRootSelector(state =>
    selectNotesIsLoading(state, { customerId: personId }),
  );

  const [tableParams, setTableParams] = useState(() => {
    return makeTableParams([], null);
  });

  useEffect(() => {
    if (isOpen) {
      dispatch(actions.fetchPersonNotes(personId, module, tableParams));
    }
  }, [dispatch, module, personId, tableParams, isOpen]);

  const handleChangeTableProps = useCallback(
    (tableProps: ITableParams): void => {
      setTableParams(tableProps);
    },
    [setTableParams],
  );

  const rows = useMemo(
    () =>
      notes.toJS().map(
        (item: INote): ITableRow => ({
          id: item.id || '',
          cells: [
            {
              label: '',
              cellComponent: item.author ? <EmployeeLinkWithAvatar employee={item.author} /> : null,
            },
            {
              label: item.noteCode?.title || '',
            },
            { label: item.alertAtNextCheckIn ? 'True' : 'False' },
            {
              label: '',
              cellComponent: (
                <Tooltip title={item.text}>
                  <Typography
                    style={{
                      display: '-webkit-box',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {item.text}
                  </Typography>
                </Tooltip>
              ),
            },
            {
              label: timezoneMoment(item.createdDate).format(DEFAULT_DATE_TIME_FORMAT) || '',
            },
          ],
        }),
      ),
    [notes, timezoneMoment],
  );

  return (
    <DialogComponent
      title={<FormattedMessage {...notesMessages.notes} />}
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      hideFooterButtons
    >
      <Table
        rows={rows}
        headerOptions={headerOptions}
        isLoading={notesIsLoading}
        page={notesMeta?.get('page')}
        totalRows={notesMeta?.get('total')}
        onChangeParams={handleChangeTableProps}
        tableParams={tableParams}
        hideSearchInput
      />
    </DialogComponent>
  );
};

export default memo(ViewAllNotesModal);
